import React, { forwardRef } from 'react';
import LayoutWithSidebars from '../components/LayoutWithSidebars';
import FileUpload from '../components/FileUpload';
import FilesTable from '../components/FilesTable';
import { useState, useEffect, useRef } from 'react';   
import { useAuth } from '../contexts/AuthContext';
import { database } from '../supabase';
import { deleteUserDocument } from '../firebase-setup';
import { Folder, Check, Menu } from 'lucide-react';
import './MyFiles.css';
import ChatModal from '../components/ChatModal';
import FlashcardsModal from '../components/FlashcardsModal';
import QuizModal from '../components/QuizModal';
const MyFiles = forwardRef(({ toggleSidebar }, ref) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingModal, setLoadingModal] = useState(false);
    const [isDragOverScreen, setIsDragOverScreen] = useState(false);
    const [successTip, setSuccessTip] = useState(false);
    const { user } = useAuth();

    // Modal states for exact Dashboard modals
    const [showChat, setShowChat] = useState(false);
    const [showFlashcards, setShowFlashcards] = useState(false);
    const [showQuizModal, setShowQuizModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);

    const toggleFlashcards = () => {
        // If no document is selected, show a message and don't open the modal
        if (!selectedDocument) {
            alert('Please select a document first by clicking on a file in the list below, then try again.');
            return;
        }
        setShowFlashcards(!showFlashcards);
    };

    const toggleQuiz = () => {
        // If no document is selected, show a message and don't open the modal
        if (!selectedDocument) {
            alert('Please select a document first by clicking on a file in the list below, then try again.');
            return;
        }
        setShowQuizModal(!showQuizModal);
    };

    const toggleChat = () => {
        // If no document is selected, show a message and don't open the modal
        if (!selectedDocument) {
            alert('Please select a document first by clicking on a file in the list below, then try again.');
            return;
        }
        setShowChat(!showChat);
    };

    // Modal trigger functions
    const handleChatModal = (document) => {
        setSelectedDocument(document);
        setShowChat(true);
    };

    const handleFlashcardsModal = (document) => {
        setSelectedDocument(document);
        setShowFlashcards(true);
    };

    const handleQuizModal = (document) => {
        setSelectedDocument(document);
        setShowQuizModal(true);
    };

    const handleDeleteDocument = async (document) => {
        if (!window.confirm(`Are you sure you want to delete "${document.document_name || document.name}"? This action cannot be undone.`)) {
            return;
        }

        try {
            // Simple approach: Remove from local state immediately
            // This gives instant feedback while the actual deletion happens
            setDocuments(prev => prev.filter(doc => 
                doc.id !== document.id && 
                doc.document_id !== document.document_id && 
                doc.uuid !== document.uuid
            ));
            
            // Show success message immediately
            setSuccessTip(true);
            setTimeout(() => setSuccessTip(false), 3000);

            // Try to delete from backend (optional - won't affect UI)
            try {
                // Try different possible endpoints
                const deleteEndpoints = [
                    'https://celebrated-charisma-production-5701.up.railway.app/delete_document',
                    'https://celebrated-charisma-production-5701.up.railway.app/remove_document',
                    'https://celebrated-charisma-production-5701.up.railway.app/documents/delete'
                ];

                let deleted = false;
                for (const endpoint of deleteEndpoints) {
                    try {
                        const response = await fetch(endpoint, {
                            method: 'DELETE',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                user_id: user.uid,
                                document_id: document.id || document.document_id || document.uuid
                            })
                        });

                        if (response.ok) {
                            deleted = true;
                            break;
                        }
                    } catch (e) {
                        // Try next endpoint
                        continue;
                    }
                }

                if (!deleted) {
                    console.log('Backend delete failed, but document removed from UI');
                }
            } catch (backendError) {
                console.log('Backend deletion not available, document removed from UI only');
            }
            
        } catch (error) {
            console.error('Delete error:', error);
            alert('Failed to delete document. Please try again.');
            // Refresh the list to restore the document
            await fetchDocuments();
        }
    };

    const handleFileUpload = async (file) => {
        try {
            console.log('File uploaded:', file.name);
            setLoadingModal(true);
            
            // Use the same upload endpoint as the main page
            const formData = new FormData();
            formData.append('file', file);
            
            const currentUserId = user?.uid;
            if (!currentUserId) {
                console.log('Please sign in to upload documents');
                setLoadingModal(false);
                return;
            }
            
            const response = await fetch('https://celebrated-charisma-production-5701.up.railway.app/upload_pdf?user_id=' + encodeURIComponent(currentUserId), {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });
            
            const data = await response.json();
            console.log('Upload successful:', data);
            
            if (response.ok) {
                // Show success tip
                setSuccessTip(true);
                setTimeout(() => {
                    setSuccessTip(false);
                }, 3000);
                
                // Refresh the documents list
                await fetchDocuments();
            } else {
                console.error('Upload failed:', data);
                alert('Upload failed. Please try again.');
            }
            
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Upload failed. Please try again.');
        } finally {
            setLoadingModal(false);
        }
    };

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const userId = user?.uid;
            if (!userId) {
                console.log('No user ID found, skipping document fetch');
                setDocuments([]);
                return;
            }

            console.log('🔍 Fetching documents from Railway backend for:', userId);

            // Add timeout to prevent hanging
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

            const response = await fetch(`https://celebrated-charisma-production-5701.up.railway.app/my_documents?user_id=${userId}`, {
                method: 'GET',
                credentials: 'include',
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error('Failed to fetch documents');
            }

            const data = await response.json();
            console.log('📄 Documents fetched from Railway:', data);
            
            if (data && Array.isArray(data.documents)) {
                setDocuments(data.documents);
            } else {
                console.log('No documents found or invalid response format');
                setDocuments([]);
            }

        } catch (error) {
            console.error('❌ Error fetching documents:', error);
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch documents when user is available
    useEffect(() => {
        if (user) {
            fetchDocuments();
        }
    }, [user]);

    // Drag and drop handlers for full screen animation
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragOverScreen) {
            setIsDragOverScreen(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Only hide if we're leaving the entire document
        if (!e.relatedTarget || e.relatedTarget === document.documentElement) {
            setIsDragOverScreen(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOverScreen(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const droppedFile = files[0];
            
            // Validate file type
            if (droppedFile.type === 'application/pdf') {
                handleFileUpload(droppedFile);
            } else {
                alert('Please drop a PDF file only.');
            }
        }
    };




    // Add drag event listeners
    useEffect(() => {
        document.addEventListener('dragover', handleDragOver);
        document.addEventListener('dragleave', handleDragLeave);
        document.addEventListener('drop', handleDrop);

        return () => {
            document.removeEventListener('dragover', handleDragOver);
            document.removeEventListener('dragleave', handleDragLeave);
            document.removeEventListener('drop', handleDrop);
        };
    }, [isDragOverScreen]);  
    return (
        <>
            <div className="files-content">
                {/* Mobile Menu Header */}
                <div className="files-mobile-header">
                    <div className="header-left">
                        <h1 className="files-title">My Files</h1>
                        <div className="header-left-mobile">
                            <Menu onClick={toggleSidebar} className="header-left-mobile-icon" />
                        </div>
                    </div>
                </div>
                
                <div className="files-header">
                    <h1>My Files</h1>
                    <p>Upload and manage your study materials.</p>
                </div>
                
                {/* File Upload Component */}
                <FileUpload onFileUpload={handleFileUpload} />
                
                {/* Files Table */}
                {loading ? (
                    <div className="files-loading">
                        <p>Loading your files...</p>
                    </div>
                ) : (
                    <FilesTable 
                        documents={documents} 
                        onChatModal={handleChatModal}
                        onFlashcardsModal={handleFlashcardsModal}
                        onQuizModal={handleQuizModal}
                        onDeleteDocument={handleDeleteDocument}
                    />
                )}
            </div>

            {/* Success Tip */}
            {successTip && (
                <div className="success-tip">
                    <div className="success-tip-content">
                        <Check className="success-tip-icon" />
                        <h1>File uploaded successfully!</h1>
                    </div>
                </div>
            )}

            {/* Loading Modal */}
            {loadingModal && (
                <div className="loading-modal">
                    <div className="loading-modal-content">
                        <h1>Uploading your PDF...</h1>
                        <div className="loading-modal-spinner"></div>
                    </div>
                </div>
            )}

            {/* Full-screen drag overlay */}
            {isDragOverScreen && (
                <div className="drag-overlay">
                    <div className="drag-overlay-content">
                        <div className="drag-overlay-icon">
                            <Folder size={60} className="drag-overlay-icon-icon" />
                        </div>
                        <h2 className="drag-overlay-title">Drop Here</h2>
                        <p className="drag-overlay-subtitle">Release to upload your PDF</p>
                    </div>
                </div>
            )}

            {/* Exact Dashboard Modals */}
            <ChatModal 
                isOpen={showChat}
                onClose={() => setShowChat(false)}
                selectedDocument={selectedDocument}
                user={user}
            />

            <FlashcardsModal 
                isOpen={showFlashcards}
                onClose={() => setShowFlashcards(false)}
                selectedDocument={selectedDocument}
                user={user}
            />

            <QuizModal 
                isOpen={showQuizModal}
                onClose={() => setShowQuizModal(false)}
                selectedDocument={selectedDocument}
                user={user}
            />
        </>
    )
});

export default MyFiles;
